import { defineComponent } from "vue";
import { mapActions } from "pinia";
import { Container } from "aurelia-dependency-injection";
import Chart from "primevue/chart";

import { PermissionManager } from "../../../../../common/utilities/permission-manager";
import { useLoadingIndicator } from "../../../../Common/LoadingIndicator/store/useLoadingIndicator";
import { ApplicationEnum, ApplicationNames } from "../../../../../enums/enums";
import { TaskService } from "../../../../../services/task.service";
import { IVisitCountResponse } from "../../../../../resources-vue/vue-interfaces/i-task";
import { clubAgenciesScheduler } from "./../../../../../common/vue-helpers/modifiers/value-modifier";
import {
    getProductColorClass,
    getProductFullName
} from "../../../../../common/vue-helpers/modifiers/application-modifiers";
import { AnalyticsCategory, AnalyticsService } from "../../../../../services/analytics.service";

import CardTemplate from "../../../../../resources-vue/vue-elements/CardTemplate/CardTemplate.vue";

export default defineComponent({
    components: {
        Chart,
        CardTemplate
    },

    data() {
        return {
            _permissionManager: null,
            _taskService: null as TaskService,
            _analyticsService: null as AnalyticsService,
            visitCounts: [] as IVisitCountResponse[],
            ApplicationNamesEnum: ApplicationNames,
            isLoading: true,
            hasError: false,
            chartData: null,
            chartOptions: null,
            count: [],
            countPercentage: [],
            labels: [],
            colors: [],
            active: {},
            ApplicationEnum,
            getProductColorClass
        };
    },
    async created() {
        this._permissionManager = Container.instance.get(PermissionManager);
        this._taskService = Container.instance.get(TaskService);
        this._analyticsService = Container.instance.get(AnalyticsService);
    },

    async mounted() {
        await this.getVisitCount();
        this.chartData = this.setChartData();
        this.chartOptions = this.setChartOptions();
    },

    methods: {
        ...mapActions(useLoadingIndicator, { showLoading: "SET_LOADING" }),

        async getVisitCount() {
            try {
                this.hasError = false;
                this.isLoading = true;

                this.visitCounts = clubAgenciesScheduler(
                    await this._taskService.getVisitCount({
                        isPastOrFuture: false
                    })
                );

                this.visitCounts.sort((a: any, b: any) => {
                    return b.Counts.UnassignedVisitCount - a.Counts.UnassignedVisitCount;
                });

                this.visitCounts.forEach((item) => {
                    delete item.AgencyId;
                    this.count.push(item.Counts.UnassignedVisitCount);
                    if (item.Application == ApplicationEnum.AxxessHospiceFE) {
                        item.Application = ApplicationEnum.AxxessHospice;
                    }
                    this.labels.push(getProductFullName(item.Application));

                    switch (item.Application) {
                        case ApplicationEnum.AgencyCore:
                            this.colors.push("rgba(164, 26, 54, 1)");
                            break;
                        case ApplicationEnum.HomeCare:
                            this.colors.push("rgba(63, 92, 117, 1)");
                            break;
                        case ApplicationEnum.AxxessHospice:
                            this.colors.push("rgba(129, 0, 154, 1)");
                            break;
                        case ApplicationEnum.AxxessPalliative:
                            this.colors.push("rgba(226, 59, 137, 1)");
                            break;
                    }
                });
            } catch (error) {
                console.error(error);
                this.hasError = true;
            } finally {
                this.isLoading = false;
            }
        },

        setChartData() {
            return {
                labels: this.labels,
                datasets: [
                    {
                        label: "Visits",
                        borderWidth: 1,
                        data: this.count,
                        backgroundColor: this.colors,
                        hoverOffset: 3
                    }
                ]
            };
        },

        setChartOptions() {
            const documentStyle = getComputedStyle(document.documentElement);
            const textColor = documentStyle.getPropertyValue("--text-color");

            return {
                responsive: true,
                angle: 360,
                plugins: {
                    legend: {
                        display: false,
                        onClick: null,
                        position: "right",
                        labels: {
                            boxWidth: 0,
                            usePointStyle: false,
                            color: textColor,
                            font: {
                                family: "Open sans",
                                size: 14
                            }
                        }
                    },

                    tooltip: {
                        enabled: true,
                        usePointStyle: false,
                        displayColors: false,
                        backgroundColor: "rgba(254, 254, 255, 0.85)",
                        borderColor: "rgba(0, 0, 0, 1)",
                        bodyColor: "rgba(0, 0, 0, 1)",
                        borderWidth: 1,
                        callbacks: {
                            label: (context: any) => {
                                this.active = context;
                                let label = [context.label];

                                label.push(context.dataset.label + ":" + context.formattedValue);

                                return label;
                            }
                        }
                    }
                }
            };
        },

        redirectToUnassignedVisit(application: number) {
            let action = "";
            switch (application) {
                case ApplicationEnum.AxxessHospice:
                    action = "Unassigned Visits HOS";
                    break;

                case ApplicationEnum.AxxessPalliative:
                    action = "Unassigned visits PAL";
                    break;

                case ApplicationEnum.HomeCare:
                    action = "Unassigned Visits HC";
                    break;

                default:
                    action = "Unassigned Visits";
            }
            this._analyticsService.logEvent({
                category: AnalyticsCategory.CentralDashboard,
                action: action
            });
            if (application == ApplicationEnum.AxxessHospice) {
                application = ApplicationEnum.AxxessHospiceFE;
            }
            this.$router.push(`/axxess-central/schedule-center/unassigned-patient-visits?application=${application}`);
        }
    }
});
